"use client";

import _ from "lodash";
import { useEffect } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { twMerge } from "tailwind-merge";
import { StoreApi, UseBoundStore } from "zustand";

import { AnimatedButtonWrapper } from "@/app/(application)/(normal)/create/components/input/AnimatedButtonWrapper";
import { AutoTuneButton } from "@/app/(application)/(normal)/create/components/input/AutoTuneButton";
import { HyperParameters } from "@/app/(application)/(normal)/create/components/input/HyperParameters";
import { LockedArtist } from "@/app/(application)/(normal)/create/components/input/LockedArtist";
import { MultiFileCoverInput } from "@/app/(application)/(normal)/create/components/input/multiFileCoverInput/MultiFileCoverInput";
import { PrepareAudioSettings } from "@/app/(application)/(normal)/create/components/input/PrepareAudioSettings";
import { QuickTuneKnobs } from "@/app/(application)/(normal)/create/components/input/QuickTuneKnobs";
import { TtvConversionInput } from "@/app/(application)/(normal)/create/components/input/ttv/TtvConversionInput";
import { Button } from "@/components/buttons/Button";
import { FileCoverInput } from "@/components/fileCoverInput/FileCoverInput";
import { LinkButton } from "@/components/links/LinkButton";
import { FileCoverInputWithRecorderSkeleton } from "@/components/skeletons/input/FileCoverInputWithRecorderSkeleton";
import { Heading3 } from "@/components/text/Heading3";
import { SoftText } from "@/components/text/SoftText";
import { AudioQualityFilePresentation } from "@/components/uploadFilePresentation/AudioQualityFilePresentation";
import { UploadFilePresentation } from "@/components/uploadFilePresentation/UploadFilePresentation";
import {
  MAX_NUMBER_OF_CONVERSION_INPUTS,
  MINUTES_MAX_UPLOAD_CONVERSION,
} from "@/constants/constants";
import {
  conversionNamespace,
  getConversionInputNamespace,
} from "@/constants/namespaces";
import { hasSeenTutorialTrueAction } from "@/data/auth/actions/hasSeenTutorialTrueAction";
import { useAudioInput } from "@/lib/zustand/useAudioInput";
import { useConversionGuideModal } from "@/lib/zustand/useConversionGuideModal";
import { useConversionHyperParameters } from "@/lib/zustand/useConversionHyperParameters";
import { useConversionIsSubmitting } from "@/lib/zustand/useConversionIsSubmitting";
import { useNamedPitchCorrection } from "@/lib/zustand/usePitchCorrection";
import { useNamedSelectedArtist } from "@/lib/zustand/useSelectedArtist";
import { useTutorial } from "@/lib/zustand/useTutorial/useTutorial";
import { IndividualConversion } from "@/types/defs/individualConversion";
import type { TtvConversionSources } from "@/types/defs/ttvConversionSource";
import {
  AudioInputState,
  MixedModelConstructedNoDates,
  ModelNoDates,
  UserNoDates,
} from "@/types/types";
import { getArtistInstrumentModelMixedModel } from "@/utils/artistInstrumentModelMixedModel/get/getArtistInstrumentModelMixedModel";
import { isInstrumentTypeGuard } from "@/utils/artistInstrumentModelMixedModel/is/isInstrumentTypeGuard";
import { getModelPitchRange } from "@/utils/audio/quality/getModelPitchRange";
import { isAudioInputActive } from "@/utils/audioInput/is/isAudioInputActive";
import { isAudioInputCover } from "@/utils/audioInput/is/isAudioInputCover";
import { isAudioInputFile } from "@/utils/audioInput/is/isAudioInputFile";
import { isAudioInputNull } from "@/utils/audioInput/is/isAudioInputNull";
import { handleSubmitConversion } from "@/utils/handleSubmitConversion";
import { useAudioQuality } from "@/utils/hooks/audioQuality/useAudioQuality";
import { usePitchesToConversionBackend } from "@/utils/hooks/usePitchesToConversionBackend";
import { isSinglePitch } from "@/utils/isSinglePitch";
import { isAllowedArtist } from "@/utils/subscriptionLevels/isAllowedArtist";

export function Input({
  user,
  models,
  mixedModelsConstructed,
  simplyUser,
  showCoverInput,
  shouldShowLoadState,
  ttvSource = null,
}: {
  user: UserNoDates | null;
  models: ModelNoDates[] | null;
  mixedModelsConstructed: MixedModelConstructedNoDates[] | null;
  simplyUser: boolean;
  showCoverInput: boolean;
  shouldShowLoadState: boolean;
  ttvSource?: TtvConversionSources | null;
}) {
  const isSubmitting = useConversionIsSubmitting((state) => state.isSubmitting);
  const setIsSubmitting = useConversionIsSubmitting(
    (state) => state.setIsSubmitting
  );

  const pitch = useConversionHyperParameters((state) => state.pitch);
  const conversionStrength = useConversionHyperParameters(
    (state) => state.conversionStrength
  );
  const removeBackgroundVocals = useConversionHyperParameters(
    (state) => state.removeBackgroundVocals
  );
  const deNoise = useConversionHyperParameters((state) => state.deNoise);
  const resetInstrumentalAndReverb = useConversionHyperParameters(
    (state) => state.resetInstrumentalAndReverb
  );

  const reset = useConversionHyperParameters((state) => state.reset);

  const conversionId = useTutorial((state) => state.conversionId);
  const isActive = useTutorial((state) => state.isActive);
  const openWelcomeModal = useTutorial((state) => state.openWelcomeModal);

  // Refresh all the hyper parameters when user navigates to the input page (or when simplyUser changes)
  useEffect(() => {
    reset(simplyUser);
  }, [simplyUser]);

  useEffect(() => {
    const handleOpenTutorial = async () => {
      if (user !== null && !user.hasSeenTutorial && !isActive) {
        // For now, we only automatically open the tutorial on desktop. If the user is on mobile we set hasSeenTutorial to true since we assume they've then used Audimee. If the user is on test we open otherwise we set hasSeenTutorial to true
        if (!isMobile && !isTablet) {
          // A handoff from Text to vocals keeps the tutorial for a later visit instead of covering the transferred takes.
          if (ttvSource === null) {
            openWelcomeModal();
          }
        } else {
          await hasSeenTutorialTrueAction();
        }
      }
    };

    handleOpenTutorial();
  }, [user, ttvSource]);

  // File input stuff //////////////////////////
  const conversionInputs: AudioInputState[] = [];
  const useConversionInputs: UseBoundStore<StoreApi<AudioInputState>>[] = [];

  for (let i = 0; i < MAX_NUMBER_OF_CONVERSION_INPUTS; i++) {
    const useConversionInputStore = useAudioInput((state) =>
      state.getNamedStore(getConversionInputNamespace(i))
    );

    useConversionInputs.push(useConversionInputStore);

    const conversionInput = useConversionInputStore((state) => state);

    conversionInputs.push(conversionInput);
  }

  const activeConversionInputs = conversionInputs.filter((inputFileCover) =>
    isAudioInputActive(inputFileCover)
  );

  const emptyConversionInputs = conversionInputs.filter((inputFileCover) =>
    isAudioInputNull(inputFileCover)
  );

  const numberOfActiveConversionInputs = activeConversionInputs.length;

  // Files render sorted by inputOrder (fill order), so the first displayed file can sit at a later slot index after deleting and re-adding files.
  const firstConversionFileIndex = conversionInputs.reduce(
    (firstIndex, conversionInput, index) => {
      if (!isAudioInputFile(conversionInput)) {
        return firstIndex;
      }
      const currentFirst = conversionInputs[firstIndex];
      if (
        currentFirst === undefined ||
        !isAudioInputFile(currentFirst) ||
        conversionInput.inputOrder < currentFirst.inputOrder
      ) {
        return index;
      }
      return firstIndex;
    },
    -1
  );

  // index or -1
  const indexOfFirstActiveConversionInput = conversionInputs.findIndex(
    (conversionInput) => isAudioInputActive(conversionInput)
  );

  const conversionInputNamespaceForPitchEditing = getConversionInputNamespace(
    indexOfFirstActiveConversionInput === -1 ? 0 : (
      indexOfFirstActiveConversionInput
    )
  );

  let currentAudioLengthS = 0;
  for (const activeConversionInput of activeConversionInputs) {
    if (isAudioInputFile(activeConversionInput)) {
      if (activeConversionInput.selectedFileCover.selectedSound !== null) {
        currentAudioLengthS +=
          activeConversionInput.selectedFileCover.selectedSound.lengthS;
      }
    }
  }

  const isCoverSelected =
    numberOfActiveConversionInputs === 1 &&
    isAudioInputCover(activeConversionInputs[0]);

  const useSelectedArtist = useNamedSelectedArtist((state) =>
    state.getNamedStore(conversionNamespace)
  );
  const selectedArtistId = useSelectedArtist((state) => state.selectedArtistId);
  const setSelectedModelPitchRange = useSelectedArtist(
    (state) => state.setSelectedModelPitchRange
  );

  const artistInstrumentModelMixedModel = getArtistInstrumentModelMixedModel(
    selectedArtistId,
    models,
    mixedModelsConstructed
  );

  const isInstrumentSelected =
    artistInstrumentModelMixedModel !== null &&
    isInstrumentTypeGuard(artistInstrumentModelMixedModel);

  const {
    badges: isolationBadges,
    pitchRecommendation,
    removeInstrumental,
    deReverbDeEcho,
    shouldShowAutoSettingsBadge,
  } = useAudioQuality(
    activeConversionInputs,
    isInstrumentSelected,
    artistInstrumentModelMixedModel
  );

  // Reset user overrides when there's nothing to apply them to (no inputs or an instrument model is selected). Without this the user's prior toggles would persist into a fresh upload, fighting the new recommendation.
  useEffect(() => {
    if (numberOfActiveConversionInputs === 0 || isInstrumentSelected) {
      resetInstrumentalAndReverb();
    }
  }, [
    numberOfActiveConversionInputs,
    isInstrumentSelected,
    resetInstrumentalAndReverb,
  ]);

  // The store-level audio-quality check runs outside React and can't read the server-fetched `models` prop, so resolve the selected voice's pitch range here and publish it for the store to read.
  useEffect(() => {
    setSelectedModelPitchRange(
      getModelPitchRange({
        selectedModel: artistInstrumentModelMixedModel,
        shouldGetArtistsSweetSpot: true,
      })
    );
  }, [artistInstrumentModelMixedModel, setSelectedModelPitchRange]);

  const showSimpleInputComponent =
    // If there is no input file cover at all
    numberOfActiveConversionInputs === 0 ||
    // If there is one input file cover and it's a cover
    isCoverSelected;

  //////////////////////////

  const pitchesToConversionBackend = usePitchesToConversionBackend({
    inputNamespace: conversionInputNamespaceForPitchEditing,
    namespacePitchCorrection: conversionNamespace,
    nameSpaceKey: conversionNamespace,
  });

  const setConversionGuideModalOpen = useConversionGuideModal(
    (state) => state.setOpen
  );

  const usePitchCorrection = useNamedPitchCorrection((state) =>
    state.getNamedStore(conversionNamespace)
  );

  const unset = usePitchCorrection((state) => state.unset);

  const clearAllInput = () => {
    conversionInputs.forEach((conversionInput) => {
      conversionInput.clearInput();
    });

    unset();
  };

  const clearInputIndex = (index: number) => {
    conversionInputs[index].clearInput();
    unset();
  };

  const handleConvert = async () => {
    setIsSubmitting(true);

    try {
      // Validate required data
      if (artistInstrumentModelMixedModel === null) {
        throw new Error("No model found");
      }

      if (user === null) {
        throw new Error("No user found");
      }

      // Calculate pitch values
      const singlePitch = isSinglePitch(pitch);
      const pitchValues =
        singlePitch ?
          [pitch]
          // range end is not inclusive so we add 1
        : _.range(pitch[0], pitch[1] + 1);

      // Get pitches for conversion backend (only for single file)
      let pitchesToSend: null | number[] = null;
      if (
        numberOfActiveConversionInputs === 1 &&
        pitchesToConversionBackend !== null &&
        pitchesToConversionBackend.dirty
      ) {
        pitchesToSend = await pitchesToConversionBackend.pitches;
      }

      // Create individual conversions for each pitch value
      const individualConversions: IndividualConversion[] = pitchValues.map(
        (pitchApp) => ({
          conversionId: isActive && singlePitch ? conversionId : null,
          artistInstrumentModelMixedModel: artistInstrumentModelMixedModel,
          pitch: pitchApp,
          pitches: pitchesToSend,
          conversionMultiId: null,
          // Only used for harmony
          harmonyKey: null,
          octaveShift: null,
          pan: null,
          timeShift: null,
        })
      );

      // Submit conversion
      await handleSubmitConversion({
        individualConversions: individualConversions,
        clearInput: clearAllInput,
        user,
        conversionStrength,
        removeInstrumental,
        removeBackgroundVocals,
        deReverbDeEcho,
        deNoise,
        conversionGroupId: null,
        useConversionInputs,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const conversionAvailable =
    numberOfActiveConversionInputs > 0 && user !== null;

  let allowedArtist = true;
  if (
    artistInstrumentModelMixedModel !== null &&
    "meta" in artistInstrumentModelMixedModel
  ) {
    allowedArtist = isAllowedArtist(
      user === null ? null : user.stripeSubscriptionId,
      artistInstrumentModelMixedModel
    );
  }

  return (
    <div>
      <div
        className={twMerge(
          "relative w-full flex-1 rounded-3xl bg-gray-50 p-4 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:p-8",
          conversionAvailable && "pb-0 sm:pb-0"
        )}
      >
        <Heading3 elementType="h3">Input</Heading3>
        {ttvSource !== null && (
          <TtvConversionInput
            key={
              ttvSource.status === "ready" ?
                ttvSource.sounds.map((sound) => sound.id).join(",")
              : ttvSource.soundIds.join(",")
            }
            source={ttvSource}
          />
        )}
        {allowedArtist ?
          <>
            <SoftText elementType="p" className="mb-4 mt-1">
              Make sure your audio is dry and free from effects. You can convert
              multiple vocals concurrently.{" "}
              <LinkButton
                onClick={setConversionGuideModalOpen}
                className="text-sm"
              >
                More instructions
              </LinkButton>
              .
            </SoftText>
            <div className="flex min-h-[170px] w-full items-center">
              <div className="w-full">
                {shouldShowLoadState && <FileCoverInputWithRecorderSkeleton />}
                {!shouldShowLoadState && showSimpleInputComponent && (
                  <FileCoverInput
                    user={user}
                    checkSubscription={true}
                    audioContext={null}
                    audioLengthMinLimitMinutes={null}
                    audioLengthMaxLimitMinutes={MINUTES_MAX_UPLOAD_CONVERSION}
                    showRecorder={true}
                    adjustAcoustic={true}
                    showSelectCoverText={true}
                    showCover={showCoverInput}
                    // This is only relevant if there is 1 active conversion input so we can just take the first one
                    setInputCover={emptyConversionInputs[0].setInputCover}
                    setInputFiles={emptyConversionInputs.map(
                      (conversionInput) => conversionInput.setInputFile
                    )}
                    presentation={
                      <UploadFilePresentation
                        selectedFileCover={
                          // This is only relevant if there is 1 active conversion input so we can just take the first one
                          activeConversionInputs.length > 0 ?
                            activeConversionInputs[0].selectedFileCover
                          : null
                        }
                        clearInput={clearAllInput}
                        hideClearButton={isSubmitting}
                        renderFilePresentation={(selectedFile) => (
                          <AudioQualityFilePresentation
                            surface="conversion"
                            selectedFile={selectedFile}
                            clearInput={clearAllInput}
                            hideClearButton={isSubmitting}
                            shouldShowAudioQualityDescription={true}
                            showAutoSettingsBadge={false}
                            isInstrumentSelected={isInstrumentSelected}
                          />
                        )}
                      />
                    }
                    currentAudioLengthS={currentAudioLengthS}
                    maxMultiAudioLengthMinutes={null}
                    multipleFiles={true}
                    empty={numberOfActiveConversionInputs === 0}
                  />
                )}
                {!shouldShowLoadState && !showSimpleInputComponent && (
                  <MultiFileCoverInput
                    audioInputs={conversionInputs}
                    hideClearButton={isSubmitting}
                    user={user}
                    setInputFiles={emptyConversionInputs.map(
                      (conversionInput) => conversionInput.setInputFile
                    )}
                    currentAudioLengthS={currentAudioLengthS}
                    clearInput={clearInputIndex}
                    unset={unset}
                    submitting={isSubmitting}
                    shouldShowAudioLength={false}
                    checkSubscription={true}
                    maxSingleAudioLengthMinutes={MINUTES_MAX_UPLOAD_CONVERSION}
                    maxNumberOfInputs={MAX_NUMBER_OF_CONVERSION_INPUTS}
                    maxMultiAudioLengthMinutes={null}
                    renderFilePresentation={(audioInput, index) => (
                      <AudioQualityFilePresentation
                        surface="conversion"
                        key={index}
                        selectedFile={audioInput.selectedFileCover}
                        clearInput={() => clearInputIndex(index)}
                        hideClearButton={isSubmitting}
                        shouldShowAudioQualityDescription={
                          index === firstConversionFileIndex
                        }
                        showAutoSettingsBadge={shouldShowAutoSettingsBadge}
                        isInstrumentSelected={isInstrumentSelected}
                      />
                    )}
                  />
                )}
              </div>
            </div>

            <div className="relative">
              <HyperParameters
                isInstrumentSelected={isInstrumentSelected}
                user={user}
                quickTuningKnobs={
                  <QuickTuneKnobs
                    noKeyIsAnOption={true}
                    onEnd={() => {}}
                    conversionNamespace={
                      conversionInputNamespaceForPitchEditing
                    }
                    namespace={conversionNamespace}
                  />
                }
                shouldShowLoadState={shouldShowLoadState}
                pitchEditorButton={
                  <AutoTuneButton
                    conversionNamespace={
                      conversionInputNamespaceForPitchEditing
                    }
                    namespace={conversionNamespace}
                    keyNamespace={conversionNamespace}
                    hubertizationNamespace={conversionNamespace}
                    disableDiatonicTranspose={false}
                    user={user}
                    track={42}
                    artistInstrumentModelMixedModel={
                      artistInstrumentModelMixedModel
                    }
                    disabledExternal={false}
                  />
                }
                hideEditPitchStuff={numberOfActiveConversionInputs > 1}
                pitchRecommendation={pitchRecommendation}
              />

              <PrepareAudioSettings
                isCoverSelected={isCoverSelected}
                isInstrumentSelected={isInstrumentSelected}
                shouldShowLoadState={shouldShowLoadState}
                instrumentalRemoval={isolationBadges.instrumental}
                reverbRemoval={isolationBadges.reverb}
                removeInstrumental={removeInstrumental}
                deReverbDeEcho={deReverbDeEcho}
                shouldShowAutoSettingsBadge={shouldShowAutoSettingsBadge}
              />

              <AnimatedButtonWrapper isVisible={conversionAvailable}>
                <Button
                  dataTutorial={
                    isSubmitting ?
                      "convert-button-submitting"
                    : "convert-button"
                  }
                  // testId={`conversion-id-input-${conversionId}`}
                  disabled={isSubmitting}
                  variant="primary"
                  loading={isSubmitting}
                  onClick={handleConvert}
                >
                  Convert
                </Button>
              </AnimatedButtonWrapper>
            </div>
          </>
        : <LockedArtist
            namespace={conversionNamespace}
            user={user}
            models={models}
            mixedModelsConstructed={mixedModelsConstructed}
          />
        }
      </div>
    </div>
  );
}
